import React, { useEffect, useState } from "react";
import {
  sendSchedule,
  emptyWeekSchedule,
  WeekSchedule,
} from "../../services/schedule";
import ScheduleEditor from "./ScheduleEditor";
import Button from "./Button";
import IconButton from "./IconButton";
import CloseIcon from "@mui/icons-material/Close";

type Target = { name: string; displayName?: string };

type Props = {
  open: boolean;
  onClose: () => void;
  buildingName?: string;
  targets: Target[];
};

type Result = {
  name: string;
  displayName?: string;
  status: "applied" | "failed";
  error?: string;
};

export default function BuildingScheduleModal({
  open,
  onClose,
  buildingName,
  targets,
}: Props) {
  const [schedule, setSchedule] = useState<WeekSchedule>(emptyWeekSchedule());
  const [active, setActive] = useState(true);
  const [applying, setApplying] = useState(false);
  const [results, setResults] = useState<Result[] | null>(null);

  // Reset to a clean editor each time the modal opens.
  useEffect(() => {
    if (open) {
      setSchedule(emptyWeekSchedule());
      setActive(true);
      setApplying(false);
      setResults(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !applying) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, applying]);

  if (!open) return null;

  const applyToAll = async () => {
    setApplying(true);
    const collected: Result[] = [];
    for (const t of targets) {
      try {
        await sendSchedule(t.name, schedule, active);
        collected.push({
          name: t.name,
          displayName: t.displayName,
          status: "applied",
        });
      } catch (err) {
        collected.push({
          name: t.name,
          displayName: t.displayName,
          status: "failed",
          error: err instanceof Error ? err.message : String(err),
        });
      }
      // Update incrementally so the user sees progress.
      setResults([...collected]);
    }
    setApplying(false);
  };

  const appliedCount = results?.filter((r) => r.status === "applied").length ?? 0;
  const failedCount = results?.filter((r) => r.status === "failed").length ?? 0;
  const done = results != null && !applying;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Set schedule for all sensors"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={() => {
        if (!applying) onClose();
      }}
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-card bg-white text-ink p-6 shadow-depth border border-black/5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-xl font-semibold">Schedule All Sensors</h2>
          <IconButton
            label="Close"
            onClick={onClose}
            disabled={applying}
            className="!w-8 !h-8 !bg-black/[0.04] !text-ink !border-black/10 hover:!bg-black/10"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
        <p className="text-ink-3 text-sm mb-5">
          Applies this schedule to{" "}
          <b>
            {targets.length} power-cutoff sensor
            {targets.length === 1 ? "" : "s"}
          </b>
          {buildingName ? ` in ${buildingName}` : ""}. Each sensor's existing
          schedule is overwritten.
        </p>

        {results ? (
          <div>
            <div className="mb-4 flex flex-wrap gap-3 text-sm">
              <span className="rounded-chip bg-alert-success/10 text-alert-success px-3 py-1 font-medium">
                {appliedCount} Applied
              </span>
              {failedCount > 0 && (
                <span className="rounded-chip bg-alert-critical/10 text-alert-critical px-3 py-1 font-medium">
                  {failedCount} Failed
                </span>
              )}
              {applying && (
                <span className="text-ink-3">
                  Applying… ({results.length}/{targets.length})
                </span>
              )}
            </div>
            <ul className="space-y-1 max-h-64 overflow-y-auto">
              {results.map((r) => (
                <li
                  key={r.name}
                  className="flex items-center justify-between text-sm border-b border-black/5 py-1.5"
                >
                  <span className="font-medium">
                    {r.displayName || r.name}
                  </span>
                  <span
                    className={
                      r.status === "applied"
                        ? "text-alert-success"
                        : "text-alert-critical"
                    }
                    title={r.error}
                  >
                    {r.status === "applied" ? "Applied" : "Failed"}
                  </span>
                </li>
              ))}
            </ul>
            {done && failedCount > 0 && (
              <p className="text-ink-3 text-xs mt-3">
                Failed sensors are usually offline. They won't pick up this
                schedule until you apply it again while they're online.
              </p>
            )}
            {done && (
              <div className="flex justify-end gap-3 mt-5 pt-4 border-t border-black/10">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setResults(null)}
                  className="!text-ink !border-black/15 hover:!bg-black/[0.04]"
                >
                  Edit Schedule
                </Button>
                <Button variant="primary" size="sm" onClick={onClose}>
                  Done
                </Button>
              </div>
            )}
          </div>
        ) : (
          <>
            <ScheduleEditor
              value={schedule}
              schedulerActive={active}
              onChange={(next, nextActive) => {
                setSchedule(next);
                setActive(nextActive);
              }}
            />
            <div className="flex justify-end gap-3 mt-5 pt-4 border-t border-black/10">
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="!text-ink !border-black/15 hover:!bg-black/[0.04]"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={applyToAll}
                disabled={targets.length === 0}
              >
                Apply to All
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
